import React from 'react';
import { TFunction, useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import { TagName } from '../../../../utils/tagName.const';
import { DateAndTimeDataHooks } from './dataHooks';
import { DateTimeFormatter } from '@wix/bookings-date-time';
import Text, { TextType } from '../../Text/Text';
import { useEditorContext } from '../../Hooks/useEditorContext';
import { classes } from './DateAndTime.st.css';
import { FormService } from '../../../../utils/mappers/serviceMapper/service.mapper';
import { isPastDate } from '../../../../utils/time';
import { FormNestedSlot } from '../../../../types/formSelectedSlots';
import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import {
  allDayCalendarDaysInTimeZone,
  allDayExclusiveEndToInclusiveInstant,
  getAllDayTimeZone,
} from '@wix/bookings-checkout-common-components';
import { ExperimentsConsts } from '../../../../consts/experiments';

export interface DateAndTimeProps {
  totalNumberOfSessions: number;
  slot: FormNestedSlot;
  service: FormService;
  isBlackOnly?: boolean;
}
const DateAndTime: React.FC<DateAndTimeProps> = ({
  service,
  totalNumberOfSessions,
  slot,
  isBlackOnly,
}) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const editorContext = useEditorContext();
  const { startDate, timezone, endDate, allDay } = slot;
  const isCourse = service.type === ServiceType.COURSE;
  const { dateRegionalSettingsLocale } = useFormComponentContext();
  const isAllDayMultiDayEnabled = experiments.enabled(
    ExperimentsConsts.AllDayMultiDayEvents,
  );
  const isStartedEnabled = experiments.enabled(
    ExperimentsConsts.SubscriptionPricingUoU,
  );

  const shouldSendTimezone =
    isCourse || startDate.at(startDate.length - 1) === 'Z';
  const resolvedTimezone = shouldSendTimezone ? timezone : undefined;
  const isAllDay = isAllDayMultiDayEnabled && allDay;

  const endDateAndTime =
    !isAllDay && shouldShowEndDate(slot, service)
      ? getDateAndTime({
          startDate: endDate,
          dateRegionalSettingsLocale,
          totalNumberOfSessions,
          t,
          timezone: resolvedTimezone,
          isStartedEnabled,
        })
      : undefined;

  const dateAndTime = editorContext.isDummy
    ? t('app.dummy-data.date-and-time')
    : isAllDay
      ? getAllDayDateDisplay({
          startDate,
          endDate,
          timezone,
          dateRegionalSettingsLocale,
          t,
        })
      : getDateAndTime({
          startDate: startDate!,
          dateRegionalSettingsLocale,
          totalNumberOfSessions,
          t,
          timezone: resolvedTimezone,
          isStartedEnabled,
        });

  return (
    <div className={classes.root}>
      <Text
        type={TextType.Primary}
        data-hook={DateAndTimeDataHooks.SLOT_DATE_AND_TIME}
        tagName={TagName.Paragraph}
        isBlackOnly={isBlackOnly}
      >
        {dateAndTime}
      </Text>
      {endDateAndTime && (
        <Text
          type={TextType.Primary}
          data-hook={DateAndTimeDataHooks.SLOT_END_DATE_AND_TIME}
          tagName={TagName.Paragraph}
          isBlackOnly={isBlackOnly}
        >
          {endDateAndTime}
        </Text>
      )}
    </div>
  );
};

const getAllDayDateDisplay = ({
  startDate,
  endDate,
  timezone,
  dateRegionalSettingsLocale,
  t,
}: {
  startDate: string;
  endDate: string;
  timezone: string;
  dateRegionalSettingsLocale: string;
  t: TFunction;
}) => {
  const allDayTimezone = getAllDayTimeZone(timezone);
  const formatter = new DateTimeFormatter(dateRegionalSettingsLocale, allDayTimezone);
  const days = allDayCalendarDaysInTimeZone(startDate, endDate, allDayTimezone);

  const formattedStart = formatter.formatDate(startDate);

  if (days === 1) {
    return t('app.booking-details.all-day.single-day', {
      date: formattedStart,
      days,
    });
  }

  const formattedEnd = formatter.formatDate(
    allDayExclusiveEndToInclusiveInstant(endDate).toISOString(),
  );

  return t('app.booking-details.all-day.date-range', {
    startDate: formattedStart,
    endDate: formattedEnd,
    days,
  });
};

const getDateAndTime = ({
  startDate,
  dateRegionalSettingsLocale,
  totalNumberOfSessions,
  t,
  timezone,
  isStartedEnabled,
}: {
  startDate: string;
  dateRegionalSettingsLocale: string;
  totalNumberOfSessions: number;
  t: TFunction;
  timezone?: string;
  isStartedEnabled: boolean;
}) => {
  const dateAndTimeFormatter = new DateTimeFormatter(
    dateRegionalSettingsLocale!,
    timezone,
  );
  const dateAndTimeFormat = dateAndTimeFormatter.formatDateAndTime(startDate);
  const isServiceHasMultipleSessions = totalNumberOfSessions > 1;

  if (isServiceHasMultipleSessions) {
    const isInPast = isStartedEnabled && isPastDate(startDate, timezone);
    const key = isInPast
      ? 'app.booking-details.course.date-and-time.started.text'
      : 'app.booking-details.course.date-and-time.starts.text';
    return t(key, { date: dateAndTimeFormat });
  }
  return dateAndTimeFormat;
};

const shouldShowEndDate = (slot: FormNestedSlot, service: FormService) => {
  if (service.type === ServiceType.COURSE) {
    return false;
  }

  const duration =
    new Date(slot.endDate).getTime() - new Date(slot.startDate).getTime();
  return duration / (24 * 60 * 60 * 1000) >= 7;
};

export default DateAndTime;
